import React, { useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import { UserCheck, Loader2, ShieldOff, UserPlus, Lock, History, X } from 'lucide-react';
import { formatDate } from '../../../lib/utils';

const CandidateOwnership = ({ candidate, currentUser, onUpdated }) => {
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const candidateId = candidate?._id || candidate?.id;

  const loadHistory = async () => {
    if (!candidateId) return;
    setLoadingHistory(true);
    try {
      const res = await api.get(`/admin/candidates/${candidateId}/assignment-history`);
      if (res.ok) {
        const d = await res.json();
        setHistory(d.history || []);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const assignedEmail = (candidate?.assigned_to || '').toLowerCase();
  const assignedName = candidate?.assigned_to_name;
  const currentEmail = (currentUser?.email || '').toLowerCase();
  const isSuperAdmin = ['super_admin', 'superadmin', 'owner', 'root'].includes((currentUser?.role || '').toLowerCase());

  // Can the current user modify this candidate?
  const canModify = !assignedEmail || isSuperAdmin || assignedEmail === currentEmail;
  // Can the current user REASSIGN?
  const canReassign = canModify; // same logic

  useEffect(() => {
    if (!editing) return;
    setLoadingAdmins(true);
    api.get('/admin/candidates/__assignable-admins')
      .then(r => r.ok ? r.json() : { admins: [] })
      .then(d => setAdmins(d.admins || []))
      .finally(() => setLoadingAdmins(false));
  }, [editing]);

  const doAssign = async (email) => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/candidates/${candidateId}/assign`, {
        assigned_to: email || null,
      });
      if (res.ok) {
        toast.success(email ? 'Candidat assigné' : 'Assignation retirée');
        setEditing(false);
        onUpdated && onUpdated();
        // Refresh history panel if open
        if (historyOpen) loadHistory();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Impossible d'assigner");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history.length === 0) loadHistory();
  };

  const actionBadge = (a) => {
    if (a === 'assigned') return { label: 'Assignation', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
    if (a === 'reassigned') return { label: 'Réassignation', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    return { label: 'Désassignation', cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  };

  return (
    <Card className="p-3 md:p-4 border-tp-border" data-testid="candidate-ownership-card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${assignedEmail ? 'bg-tp-accent/15 text-tp-accent' : 'bg-gray-100 text-gray-500'}`}>
            {assignedEmail ? <UserCheck className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-tp-text-light">Assigné à</p>
            {assignedEmail ? (
              <p className="text-sm font-semibold text-tp-text truncate" data-testid="ownership-assignee">
                {assignedName || assignedEmail}
                {assignedEmail === currentEmail && (
                  <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">vous</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-tp-text-light italic" data-testid="ownership-unassigned">Non assigné — tous les admins peuvent modifier</p>
            )}
          </div>
        </div>

        {!canModify && (
          <div className="flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-orange-50 border border-orange-200 text-orange-700" data-testid="ownership-readonly-badge">
            <Lock className="h-3.5 w-3.5" />
            Lecture seule
          </div>
        )}

        {canReassign && !editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            disabled={saving}
            data-testid="ownership-edit-btn"
          >
            {assignedEmail ? 'Réassigner' : 'Assigner'}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleHistory}
          data-testid="ownership-history-toggle"
          className="text-tp-text-light hover:text-tp-text"
          title="Historique des assignations"
        >
          <History className="h-4 w-4" />
        </Button>
      </div>

      {editing && (
        <div className="mt-3 pt-3 border-t border-tp-border flex items-center gap-2 flex-wrap" data-testid="ownership-edit-panel">
          {loadingAdmins ? (
            <Loader2 className="h-4 w-4 animate-spin text-tp-text-light" />
          ) : (
            <Select onValueChange={doAssign} disabled={saving}>
              <SelectTrigger className="h-9 w-full max-w-xs" data-testid="ownership-select">
                <SelectValue placeholder="Choisir un admin…" />
              </SelectTrigger>
              <SelectContent>
                {admins.map((a) => (
                  <SelectItem key={a.email} value={a.email} data-testid={`ownership-option-${a.email}`}>
                    {a.name} {a.role ? <span className="text-xs text-gray-400">({a.role})</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {assignedEmail && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => doAssign(null)}
              disabled={saving}
              className="text-red-600 hover:text-red-700"
              data-testid="ownership-unassign-btn"
            >
              <ShieldOff className="h-4 w-4 mr-1" /> Retirer
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={saving}>
            Annuler
          </Button>
        </div>
      )}

      {historyOpen && (
        <div className="mt-3 pt-3 border-t border-tp-border" data-testid="ownership-history-panel">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-tp-text-light uppercase tracking-wide flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> Historique des assignations
            </h4>
            <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(false)} className="h-6 w-6 p-0">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          {loadingHistory ? (
            <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-tp-accent" /></div>
          ) : history.length === 0 ? (
            <p className="text-xs text-tp-text-light italic text-center py-3" data-testid="ownership-history-empty">
              Aucun historique d'assignation pour ce candidat.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="ownership-history-list">
              {history.map((h) => {
                const b = actionBadge(h.action);
                return (
                  <li key={h._id || h.id} className="text-xs flex items-start gap-2 p-2 rounded bg-tp-bg/40">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium shrink-0 ${b.cls}`}>{b.label}</span>
                    <div className="flex-1 min-w-0">
                      {h.action === 'unassigned' && (
                        <p className="text-tp-text truncate">
                          <strong>{h.previous_assignee_name || h.previous_assignee}</strong> retiré
                        </p>
                      )}
                      {h.action === 'assigned' && (
                        <p className="text-tp-text truncate">
                          Assigné à <strong>{h.new_assignee_name || h.new_assignee}</strong>
                        </p>
                      )}
                      {h.action === 'reassigned' && (
                        <p className="text-tp-text truncate">
                          <strong>{h.previous_assignee_name || h.previous_assignee}</strong> → <strong>{h.new_assignee_name || h.new_assignee}</strong>
                        </p>
                      )}
                      <p className="text-[10px] text-tp-text-light mt-0.5">
                        par {h.changed_by_name || h.changed_by} · {formatDate(h.created_at)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
};

export default CandidateOwnership;
